import React, { useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { Grid, Card, Typography, Box, Button, Paper, ButtonBase } from '@mui/material';
import { MenuLibraryManagementState, MenuLibraryItem } from './menu-library-management-state';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import SelectRestaurantList from '../../component/select-restaurant/select-restaurtant-list';
import MainPageState from '../main/main-page-states';
import i18n from '../../i18n';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import { ReactComponent as MenuBookIcon } from '../../static/images/svg/auto_stories.svg';
import { ReactComponent as AddIcon } from '../../static/images/svg/mingcute_add-fill.svg';
import { ReactComponent as CloudUploadIcon } from '../../static/images/svg/iconamoon_cloud-upload-fill.svg';
/** ic:baseline-delete-forever（Figma 9809:51214 管理/删除确认条） */
import MenuLibraryMenuDetailComponent from './menu-library-menu-detail.component';
import MenuLibraryCreateMenuDialog from './menu-library-create-menu-dialog.component';
import MenuLibraryLoadCloudDishDialog from './menu-library-load-cloud-dish-dialog.component';
import MenuLibraryUploadCloudFlowDialogs from './menu-library-upload-cloud-flow-dialogs.component';
import './scss/menu-library-management.style.scss';

/**
 * 菜单库管理页面：一级菜单卡片列表、模块内详情编辑、新建一级菜单弹窗。
 * 本地 Tab：上传至云端；隧道+版本门禁后才展示本地顶栏与列表数据。
 * 云端 Tab：不校验隧道/版本，直接拉云端列表；详情：加载云端菜品仅属本地详情。
 * 本地隧道校验按模块内下拉所选门店执行（`init` → `checkPermissionByRestaurantStringId` + `get_restaurant_version_check`），不使用全局 `HookDecorator`。
 */
const MenuLibraryManagementPage: React.FC = observer(() => {
    useTranslation();

    const menuLibraryState = useLocalObservable(() => new MenuLibraryManagementState());
    const topBarState = MobxObjectsManager.get(TopBarState);
    const mainPageData = MobxObjectsManager.get(MainPageState);
    const belongedRestaurant = mainPageData.belongedRestaurant ?? [];

    useEffect(() => {
        topBarState.updateTitle('menu_library_management_page_text');
        topBarState.updateSubTitle('');
    }, []);

    /**
     * 必须在同一 effect 内先同步模块门店再 init：若拆成「仅依赖 string_id」的 effect，
     * ensure 写入 `menuLibraryRestaurant` 后会触发重渲染，string_id 从 undefined 变为具体值，
     * init 会执行两次，导致 `GET .../menus?type=0&with_dish=false&rbpSort=1` 重复请求。
     * 顶栏切换门店由 `handleRestaurantChange` 内显式 `init()` 覆盖，不依赖本 effect 的 string_id。
     */
    useEffect(() => {
        menuLibraryState.ensureMenuLibraryRestaurantFromScope(mainPageData.currentRestaurant, belongedRestaurant);
        void menuLibraryState.init();
    }, [belongedRestaurant, mainPageData.currentRestaurant]);

    useEffect(() => {
        const name = menuLibraryState.detailFirstMenu?.main_group_name;
        if (menuLibraryState.isMenuDetailOpen && name) {
            topBarState.updateSubTitle(name);
        } else {
            topBarState.updateSubTitle('');
        }
    }, [
        menuLibraryState.isMenuDetailOpen,
        menuLibraryState.detailMainGroupId,
        menuLibraryState.detailCloudMenuId,
        menuLibraryState.detailFirstMenu?.main_group_name,
    ]);

    /**
     * 切换模块内餐厅并重新校验 / 拉列表。
     * @param value - 门店 string_id
     */
    const handleRestaurantChange = (value: string) => {
        const restaurant = belongedRestaurant.find((item) => item.string_id === value);
        if (restaurant) {
            menuLibraryState.setMenuLibraryRestaurant(restaurant);
            if (menuLibraryState.isMenuDetailOpen) {
                menuLibraryState.closeMenuDetail();
            }
            void menuLibraryState.init();
        }
    };

    /**
     * 点击菜单卡片：多选模式下切换勾选，否则进入详情。
     * @param item - 列表卡片项
     */
    const handleMenuCardClick = (item: MenuLibraryItem) => {
        if (menuLibraryState.isUploadToCloudSelectMode) {
            menuLibraryState.toggleUploadMainGroupSelection(item.main_group_id);
            return;
        }
        menuLibraryState.openMenuDetail(item.main_group_id);
    };

    /**
     * 渲染本地菜单卡片（含上传多选选中态）
     * @param item 菜单项
     */
    const renderMenuCard = (item: MenuLibraryItem) => {
        // 是否处于上传多选且已勾选
        const selected =
            menuLibraryState.isUploadToCloudSelectMode &&
            menuLibraryState.isUploadMainGroupSelected(item.main_group_id);

        return (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={String(item.main_group_id)}>
                <Card
                    elevation={0}
                    className={[
                        'menu-library-card',
                        menuLibraryState.isUploadToCloudSelectMode ? 'menu-library-card--select-mode' : '',
                        selected ? 'menu-library-card--selected' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => handleMenuCardClick(item)}
                    aria-pressed={menuLibraryState.isUploadToCloudSelectMode ? selected : undefined}
                >
                    <Box className="menu-card-content">
                        <MenuBookIcon className="menu-card-icon-svg" aria-hidden />
                        {/* 稿面标题 / 日期均为正文 20 Medium */}
                        <Typography variant="body1" className="menu-card-title">
                            {item.title}
                        </Typography>
                        <Typography variant="body1" className="menu-card-date">
                            {item.subtitle}
                        </Typography>
                    </Box>
                </Card>
            </Grid>
        );
    };

    /**
     * 列表顶区：本地 / 云端 Tab（Figma 9739:39936）
     */
    const renderListTabs = () => {
        const tab = menuLibraryState.menuLibraryListTab;
        return (
            <Box className="menu-library-list-tabs" role="tablist" aria-label={i18n.t('menu_library_management_page_text')}>
                <ButtonBase
                    className={[
                        'menu-library-list-tabs__item',
                        tab === 'local' ? 'menu-library-list-tabs__item--active' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => menuLibraryState.setMenuLibraryListTab('local')}
                    role="tab"
                    aria-selected={tab === 'local'}
                >
                    {/* 激活 Tab 用正文加粗，未激活用正文 Medium */}
                    <Typography
                        component="span"
                        variant={tab === 'local' ? 'subtitle1' : 'body1'}
                        className="menu-library-list-tabs__label"
                    >
                        {i18n.t('menu_library_list_tab_local')}
                    </Typography>
                    <span
                        className={[
                            'menu-library-list-tabs__underline',
                            tab === 'local' ? 'menu-library-list-tabs__underline--active' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        aria-hidden
                    />
                </ButtonBase>
                <ButtonBase
                    className={[
                        'menu-library-list-tabs__item',
                        tab === 'cloud' ? 'menu-library-list-tabs__item--active' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => menuLibraryState.setMenuLibraryListTab('cloud')}
                    role="tab"
                    aria-selected={tab === 'cloud'}
                >
                    <Typography
                        component="span"
                        variant={tab === 'cloud' ? 'subtitle1' : 'body1'}
                        className="menu-library-list-tabs__label"
                    >
                        {i18n.t('menu_library_list_tab_cloud')}
                    </Typography>
                    <span
                        className={[
                            'menu-library-list-tabs__underline',
                            tab === 'cloud' ? 'menu-library-list-tabs__underline--active' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        aria-hidden
                    />
                </ButtonBase>
            </Box>
        );
    };

    /**
     * 上传多选底部汇总确认条（Figma 9797:49674）
     */
    const renderUploadConfirmBar = () => {
        if (!menuLibraryState.isUploadToCloudSelectMode) {
            return null;
        }
        // 已选菜单份数 / 菜品总数
        const menuCount = menuLibraryState.selectedUploadMenuCount;
        const dishCount = menuLibraryState.selectedUploadDishCount;
        const canConfirm = menuCount > 0 && !menuLibraryState.uploadToCloudSubmitting;

        return (
            <Box className="menu-library-upload-confirm-bar" role="region" aria-label={i18n.t('menu_library_upload_cloud_confirm_region')}>
                <Box className="menu-library-upload-confirm-bar__summary">
                    <CloudUploadIcon className="menu-library-upload-confirm-bar__cloud-icon" aria-hidden />
                    {/* 标题加粗 24；份数 / 菜品数用主题色强调 */}
                    <Typography variant="h3" component="p" className="menu-library-upload-confirm-bar__text">
                        <Trans
                            i18nKey="menu_library_upload_cloud_confirm_summary"
                            values={{ menuCount, dishCount }}
                            components={{
                                highlight: <Typography component="span" variant="h3" className="menu-library-upload-confirm-bar__emphasis" />,
                            }}
                        />
                    </Typography>
                </Box>
                <Box className="menu-library-upload-confirm-bar__actions">
                    <Button
                        variant="outlined"
                        className="menu-library-upload-confirm-bar__cancel"
                        onClick={() => menuLibraryState.cancelUploadToCloudSelectMode()}
                    >
                        <Typography component="span" variant="h4">
                            {i18n.t('general_button_cancel')}
                        </Typography>
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        className="menu-library-upload-confirm-bar__confirm"
                        disabled={!canConfirm}
                        onClick={() => void menuLibraryState.confirmUploadToCloud()}
                    >
                        <Typography component="span" variant="h4">
                            {i18n.t('general_button_confirm')}
                        </Typography>
                    </Button>
                </Box>
            </Box>
        );
    };

    /**
     * 渲染单张云端菜单卡片
     * @param item - 云端菜单摘要
     */
    const renderCloudMenuCard = (item: { id: string; main_group_name?: string }) => {
        const summary = menuLibraryState.cloudMenuList.find((m) => m.id === item.id);

        return (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={item.id}>
                <Card
                    elevation={0}
                    className={[
                        'menu-library-card',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => {
                        menuLibraryState.openCloudMenuDetail(item.id);
                    }}
                >
                    <Box className="menu-card-content">
                        <MenuBookIcon className="menu-card-icon-svg" aria-hidden />
                        <Typography variant="body1" className="menu-card-title">
                            {item.main_group_name || item.id}
                        </Typography>
                        <Typography variant="body1" className="menu-card-date">
                            {summary ? menuLibraryState.getCloudMenuCardSubtitle(summary) : '—'}
                        </Typography>
                    </Box>
                </Card>
            </Grid>
        );
    };

    /**
     * 云端 Tab：列表与新增云端菜单。
     */
    const renderCloudMenuList = () => {
        /** 云端 Tab 不依赖隧道门禁；有所选门店即可展示顶栏操作 */
        const showToolbar = Boolean(menuLibraryState.menuLibraryRestaurant?.string_id);

        /**
         * 云端网格内容（不展示本地隧道/版本文案）
         */
        const renderCloudGrid = () => {
            if (menuLibraryState.cloudMenuListLoading) {
                return (
                    <Box className="loading-container">
                        <Typography variant="body1">{i18n.t('general_text_loading')}</Typography>
                    </Box>
                );
            }
            if (menuLibraryState.cloudMenuList.length === 0) {
                return (
                    <Box className="empty-container">
                        <Typography variant="body1">{i18n.t('general_text_no_or_more_data')}</Typography>
                    </Box>
                );
            }
            return (
                <Grid container spacing={8}>
                    {menuLibraryState.cloudMenuList.map(renderCloudMenuCard)}
                </Grid>
            );
        };

        return (
            <>
                <Box className="menu-library-top-bar">
                    <Box className="menu-library-top-bar__left">
                        <SelectRestaurantList
                            menuLibraryTopBar
                            currentRestaurant={menuLibraryState.menuLibraryRestaurant ?? {}}
                            items={belongedRestaurant.map((item) => ({
                                label: item.nickname || '',
                                value: item.string_id || '',
                            }))}
                            value={menuLibraryState.menuLibraryRestaurant?.string_id || ''}
                            onChange={handleRestaurantChange}
                        />
                    </Box>
                    <Box className="menu-library-top-bar__right">
                        {showToolbar ? (
                            <Button
                                variant="outlined"
                                color="primary"
                                className="menu-library-top-bar__new-menu-btn"
                                onClick={() => void menuLibraryState.handleAddCloud()}
                                aria-label={i18n.t('menu_library_list_add_cloud_menu')}
                            >
                                <AddIcon className="menu-library-top-bar__new-menu-btn-icon" aria-hidden />
                                <Typography
                                    component="span"
                                    variant="h4"
                                    className="menu-library-top-bar__new-menu-btn-text"
                                >
                                    {i18n.t('menu_library_list_add_cloud_menu')}
                                </Typography>
                            </Button>
                        ) : null}
                    </Box>
                </Box>

                <Box
                    className={[
                        'menu-library-grid-container',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    {renderCloudGrid()}
                </Box>

            </>
        );
    };

    /**
     * 渲染本地菜单网格内容：拆出条件分支，避免 JSX 内多层三元表达式影响可读性。
     * @returns 加载、异常、空态或菜单卡片网格
     */
    const renderLocalMenuGridContent = () => {
        if (menuLibraryState.isLoading) {
            return (
                <Box className="loading-container">
                    <Typography variant="body1">{i18n.t('general_text_loading')}</Typography>
                </Box>
            );
        }

        if (menuLibraryState.menuLibraryTunnelErrorI18nKey) {
            return (
                <Box className="empty-container">
                    <Typography variant="body1">
                        {i18n.t(
                            menuLibraryState.menuLibraryTunnelErrorI18nKey,
                            menuLibraryState.menuLibraryTunnelErrorParams ?? {},
                        )}
                    </Typography>
                </Box>
            );
        }

        if (menuLibraryState.menuLibraryList.length === 0) {
            return (
                <Box className="empty-container">
                    <Typography variant="body1">{i18n.t('general_text_no_or_more_data')}</Typography>
                </Box>
            );
        }

        return (
            <>
                {/* Figma 菜单区 gap 32px → theme.spacing(8) */}
                <Grid container spacing={8}>
                    {menuLibraryState.menuLibraryList.map(renderMenuCard)}
                </Grid>
            </>
        );
    };

    /**
     * 渲染本地菜单列表（含顶栏与上传多选）
     */
    const renderLocalMenuList = () => {
        /** 隧道校验通过后列表顶栏才展示节假日开关与操作按钮 */
        const showListToolbarAfterTunnelOk = menuLibraryState.menuLibraryListTunnelVerified;
        // 是否处于上传多选
        const selectMode = menuLibraryState.isUploadToCloudSelectMode;

        return (
            <>
                <Box className="menu-library-top-bar">
                    <Box className="menu-library-top-bar__left">
                        <SelectRestaurantList
                            menuLibraryTopBar
                            currentRestaurant={menuLibraryState.menuLibraryRestaurant ?? {}}
                            items={belongedRestaurant.map((item) => ({
                                label: item.nickname || '',
                                value: item.string_id || '',
                            }))}
                            value={menuLibraryState.menuLibraryRestaurant?.string_id || ''}
                            onChange={handleRestaurantChange}
                        />
                        {showListToolbarAfterTunnelOk ? (
                            <Paper elevation={0} className="menu-library-top-bar__holiday-pill">
                                <Typography
                                    variant="body1"
                                    component="span"
                                    className="menu-library-top-bar__holiday-label"
                                >
                                    {i18n.t('menu_library_detail_holiday_menu_switch')}
                                </Typography>
                                <IOSSwitch
                                    className="menu-library-top-bar__holiday-switch"
                                    loading={
                                        menuLibraryState.detailLoading ||
                                        menuLibraryState.storeHolidayMenuToggleLoading ||
                                        menuLibraryState.storeHolidayMenuSettingFetchLoading
                                    }
                                    checked={menuLibraryState.storeHolidayMenuOpen}
                                    onChange={() => void menuLibraryState.toggleStoreHolidayMenu()}
                                />
                            </Paper>
                        ) : null}
                    </Box>
                    <Box className="menu-library-top-bar__right">
                        {showListToolbarAfterTunnelOk ? (
                            <>
                                <Button
                                    variant={selectMode ? 'contained' : 'outlined'}
                                    color="primary"
                                    className={[
                                        'menu-library-top-bar__upload-cloud-btn',
                                        selectMode ? 'menu-library-top-bar__upload-cloud-btn--active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => {
                                        if (selectMode) {
                                            return;
                                        }
                                        menuLibraryState.enterUploadToCloudSelectMode();
                                    }}
                                    aria-label={i18n.t('menu_library_upload_to_cloud')}
                                    aria-pressed={selectMode}
                                >
                                    <CloudUploadIcon
                                        className="menu-library-top-bar__upload-cloud-btn-icon"
                                        aria-hidden
                                    />
                                    <Typography
                                        component="span"
                                        variant="h4"
                                        className="menu-library-top-bar__upload-cloud-btn-text"
                                    >
                                        {i18n.t('menu_library_upload_to_cloud')}
                                    </Typography>
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={[
                                        'menu-library-top-bar__new-menu-btn',
                                        selectMode ? 'menu-library-top-bar__new-menu-btn--muted' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    onClick={() => void menuLibraryState.handleAdd()}
                                    disabled={selectMode}
                                    aria-label={i18n.t('menu_library_list_add_local_menu')}
                                >
                                    <AddIcon className="menu-library-top-bar__new-menu-btn-icon" aria-hidden />
                                    <Typography
                                        component="span"
                                        variant="h4"
                                        className="menu-library-top-bar__new-menu-btn-text"
                                    >
                                        {i18n.t('menu_library_list_add_local_menu')}
                                    </Typography>
                                </Button>
                            </>
                        ) : null}
                    </Box>
                </Box>

                <Box
                    className={[
                        'menu-library-grid-container',
                        selectMode ? 'menu-library-grid-container--upload-select' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    {renderLocalMenuGridContent()}
                </Box>

                {renderUploadConfirmBar()}
            </>
        );
    };

    /**
     * 列表视图（Tab + 本地/云端内容）
     */
    const renderMenuList = () => {
        return (
            <>
                {renderListTabs()}
                {menuLibraryState.menuLibraryListTab === 'cloud'
                    ? renderCloudMenuList()
                    : renderLocalMenuList()}
            </>
        );
    };

    return (
        <Box
            className={[
                'menu-library-management-page',
                menuLibraryState.isMenuDetailOpen ? 'menu-library-management-page--detail' : '',
                menuLibraryState.isMenuDetailOpen && menuLibraryState.isDetailHeaderEditMode
                    ? 'menu-library-management-page--detail-editing'
                    : '',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {menuLibraryState.isMenuDetailOpen ? (
                <MenuLibraryMenuDetailComponent state={menuLibraryState} />
            ) : (
                renderMenuList()
            )}

            <MenuLibraryCreateMenuDialog state={menuLibraryState} />
            <MenuLibraryLoadCloudDishDialog state={menuLibraryState} />
            <MenuLibraryUploadCloudFlowDialogs state={menuLibraryState} />
        </Box>
    );
});

export default MenuLibraryManagementPage;
